import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("rtf_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white border border-[#E6E2D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10">
        <div className="text-center">
          <img src={LOGO_URL} alt="Resort The Forest logo" className="h-16 w-16 object-contain mx-auto" />
          <h1 className="font-serif-display text-3xl text-[#0B3B39] mt-4">Owner Login</h1>
          <p className="text-sm text-[#4A5568] mt-2">Reservations dashboard access</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5" data-testid="admin-login-form">
          <div className="flex flex-col gap-2">
            <Label htmlFor="admin-email" className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Email</Label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@resorttheforest.com" className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="admin-password" className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39]">Password</Label>
            <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-white border-[#E6E2D8] rounded-sm px-4 py-3 h-auto text-sm" />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">{error}</p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit-btn"
            disabled={loading}
            className="bg-[#0B3B39] text-[#FDFBF7] text-xs uppercase tracking-[0.25em] font-bold px-8 py-4 rounded-sm hover:bg-[#072A28] disabled:opacity-60 transition-colors duration-300"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p className="text-center mt-6">
          <Link to="/" className="text-xs uppercase tracking-[0.2em] text-[#4A5568] hover:text-[#0B3B39] transition-colors" data-testid="back-to-site-link">
            &larr; Back to website
          </Link>
        </p>
      </div>
    </div>
  );
}
